import { isUuid } from './uuid';

// Top-level routes that are siblings of the `:slug/*` account route in
// RootRouter, so their first segment is a literal, not an account slug.
const TOP_LEVEL_SEGMENTS = new Set([
  'login',
  'logout',
  'unauthorized',
  'auth-error',
  'invitation',
  'invitations',
  'create-account',
  'welcome',
  'onboarding',
  'not-found',
  'profile',
]);

// Parent segment -> placeholder for its child, whose value is a customer-defined
// name (not a UUID) that must never reach `path_template`.
//
// `logs` is the campaign subscriber journey. Its id identifies an end customer
// and is not always a UUID, so without this it would ride on `path_template` —
// and from there onto every event fired on that page, via the page context.
//
// `category` is matched by the router without validation, so a direct visit puts
// arbitrary typed text there. Same reasoning: unbounded, and it inherits.
const DYNAMIC_NAME_PARENTS = new Map<string, string>([
  ['intents', ':intentName'],
  ['entities', ':entityName'],
  ['logs', ':subscriberId'],
  ['category', ':category'],
]);

/**
 * Collapse a raw `window.location.pathname` into a low-cardinality template for
 * Amplitude aggregation: UUIDs -> `:id`, an account slug -> `:slug`, and
 * customer-defined name segments -> their param placeholder.
 */
export const normalizePath = (pathname: string): string => {
  const segments = (pathname || '').split('/').filter(Boolean);
  if (segments.length === 0) {
    return '/';
  }
  const masked = segments.map((segment, index) => {
    if (isUuid(segment)) {
      return ':id';
    }
    if (index === 0) {
      return TOP_LEVEL_SEGMENTS.has(segment) ? segment : ':slug';
    }
    return DYNAMIC_NAME_PARENTS.get(segments[index - 1]) ?? segment;
  });
  return '/' + masked.join('/');
};

/**
 * Return the LAST (most specific) UUID segment as the high-cardinality resource
 * id, or `undefined`. Pages whose leaf is a customer-defined name (e.g. an
 * intent/entity) have no UUID resource, so the parent UUID is not reported.
 */
export const extractResourceId = (pathname: string): string | undefined => {
  const segments = (pathname || '').split('/').filter(Boolean);
  const lastIndex = segments.length - 1;
  if (lastIndex > 0 && DYNAMIC_NAME_PARENTS.has(segments[lastIndex - 1])) {
    return undefined;
  }
  for (let index = lastIndex; index >= 0; index -= 1) {
    if (isUuid(segments[index])) {
      return segments[index];
    }
  }
  return undefined;
};
